import { FormattedMessage } from "react-intl";
import Alert from "reactstrap/lib/Alert";
import Col from "reactstrap/lib/Col";
import Row from "reactstrap/lib/Row";
import Button from "reactstrap/lib/Button";
import Table from "reactstrap/lib/Table";
import { formatDate } from "../../Utilities/dateUtils";
import {
  BarRow,
  DayBars,
  FindingTabs,
  PanelCard,
  StatCard,
  findingsForTab,
  formatCompact,
  formatNumber,
} from "./DailyAnalysisParts";

// The tabs each show one kind of finding. This page shows how they are distributed,
// which is the one view none of them can give, and sends the reader on from there.
function DailyAnalysisOverview({ data, onSelectTab }) {
  const summary = data.summary || {};
  const trends = data.trends || {};
  const usage = data.usage || {};
  const shortHistory =
    typeof data.baselineDays === "number" && data.baselineDays < 14;

  const hours = (trends.requestsByHour || []).map((count, hour) => ({
    label: String(hour).padStart(2, "0"),
    count,
  }));
  const peakHour =
    hours.length > 0
      ? hours.reduce((a, b) => (b.count > a.count ? b : a), hours[0])
      : null;

  const days = (trends.requestsByDay || []).map((day) => ({
    label: formatDate(day.date, "d/M"),
    count: day.count,
  }));
  const changeFromPrevious = summary.requestsPrevious
    ? ((summary.requests - summary.requestsPrevious) /
        summary.requestsPrevious) *
      100
    : null;

  const perTab = FindingTabs.map((tab) => {
    const mine = findingsForTab(data, tab.id);
    return {
      ...tab,
      crit: mine.filter((f) => f.severity === "crit").length,
      warn: mine.filter((f) => f.severity !== "crit").length,
    };
  });

  // trafficSplit holds fractions (0.237) and is shown as percentages.
  const rawSplit = usage.trafficSplit;
  const split = rawSplit
    ? [
        {
          id: "content",
          labelId: "DailyAnalysisContentAndStatic",
          value: (rawSplit.content || 0) * 100,
        },
        {
          id: "api",
          labelId: "DailyAnalysisTrafficApi",
          value: (rawSplit.api || 0) * 100,
        },
        {
          id: "other",
          labelId: "DailyAnalysisOther",
          value: (rawSplit.other || 0) * 100,
        },
      ]
    : null;
  const splitMax = split ? Math.max(...split.map((s) => s.value)) : 0;

  return (
    <div>
      {/* A history shorter than fourteen days overstates the number of "new" anomalies.
          The banner disappears on its own once baselineDays catches up. */}
      {shortHistory ? (
        <Alert color="warning" className="da-banner">
          <FormattedMessage
            id="DailyAnalysisShortBaseline"
            values={{ days: data.baselineDays }}
          />
        </Alert>
      ) : (
        ""
      )}
      {data.headline ? (
        <p className="da-headline">
          <span>{data.headline}</span>
        </p>
      ) : (
        ""
      )}
      <Row className="da-equal">
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisRequests"
            value={formatCompact(summary.requests)}
            note={
              summary.requestsBaselineMedian ? (
                <FormattedMessage
                  id="DailyAnalysisMedian"
                  values={{
                    value: formatCompact(summary.requestsBaselineMedian),
                  }}
                />
              ) : (
                ""
              )
            }
          />
        </Col>
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisServerErrors"
            value={formatNumber(summary.errors5xx)}
          />
        </Col>
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisUnauthorized"
            value={formatCompact(summary.errors401)}
            note={
              summary.requests ? (
                <FormattedMessage
                  id="DailyAnalysisShareOfTraffic"
                  values={{
                    value: (
                      (summary.errors401 / summary.requests) *
                      100
                    ).toLocaleString("sv-SE", { maximumFractionDigits: 1 }),
                  }}
                />
              ) : (
                ""
              )
            }
            tone="crit"
          />
        </Col>
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisClientErrors"
            value={formatNumber(summary.clientErrors)}
            note={
              typeof summary.affectedUsers === "number" ? (
                <FormattedMessage
                  id="DailyAnalysisAffectedCount"
                  values={{ count: formatNumber(summary.affectedUsers) }}
                />
              ) : (
                ""
              )
            }
          />
        </Col>
      </Row>
      <Row className="da-equal">
        {days.length > 0 ? (
          <Col md="4">
            <PanelCard
              titleId="DailyAnalysisRequestsPerDay"
              subtitle={
                changeFromPrevious === null ? (
                  ""
                ) : (
                  <FormattedMessage
                    id="DailyAnalysisVsPrevious"
                    values={{
                      change: `${changeFromPrevious > 0 ? "+" : ""}${changeFromPrevious.toLocaleString(
                        "sv-SE",
                        { maximumFractionDigits: 1 },
                      )}`,
                      date: formatDate(summary.requestsPreviousDate, "d MMM"),
                    }}
                  />
                )
              }
            >
              <DayBars points={days} />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        {hours.length > 0 ? (
          <Col md="4">
            <PanelCard
              titleId="DailyAnalysisPerHour"
              subtitle={
                peakHour ? (
                  <FormattedMessage
                    id="DailyAnalysisPeakHour"
                    values={{
                      hour: peakHour.label,
                      count: formatNumber(peakHour.count),
                    }}
                  />
                ) : (
                  ""
                )
              }
            >
              <DayBars points={hours} labelEvery={6} />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        {split ? (
          <Col md="4">
            <PanelCard
              titleId="DailyAnalysisTrafficSplit"
              subtitle={<FormattedMessage id="DailyAnalysisTrafficSplitSub" />}
            >
              {split.map((row) => (
                <BarRow
                  key={row.id}
                  label={<FormattedMessage id={row.labelId} />}
                  value={row.value}
                  max={splitMax}
                  display={`${row.value.toLocaleString("sv-SE", {
                    maximumFractionDigits: 1,
                  })} %`}
                />
              ))}
            </PanelCard>
          </Col>
        ) : (
          ""
        )}
      </Row>

      <Row className="da-equal">
        {data.themes && data.themes.length > 0 ? (
          <Col md="6">
            <PanelCard
              titleId="DailyAnalysisThemes"
              subtitle={<FormattedMessage id="DailyAnalysisThemesSub" />}
            >
              <ul className="da-themes">
                {data.themes.map((theme, i) => (
                  <li key={i}>{theme}</li>
                ))}
              </ul>
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        <Col md="6">
          <PanelCard
            titleId="DailyAnalysisWhereProblems"
            subtitle={<FormattedMessage id="DailyAnalysisWhereProblemsSub" />}
          >
            <Table responsive size="sm" className="da-table">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id="DailyAnalysisSource" />
                  </th>
                  <th className="da-num">
                    <FormattedMessage id="DailyAnalysisCritical" />
                  </th>
                  <th className="da-num">
                    <FormattedMessage id="DailyAnalysisWarnings" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {perTab.map((tab) => (
                  <tr key={tab.id}>
                    <td>
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => onSelectTab(tab.id)}
                      >
                        <FormattedMessage id={tab.labelId} />
                      </Button>
                    </td>
                    <td
                      className={
                        tab.crit > 0
                          ? "da-num da-num--strong da-num--crit"
                          : "da-num"
                      }
                    >
                      {formatNumber(tab.crit)}
                    </td>
                    <td className="da-num">{formatNumber(tab.warn)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PanelCard>
        </Col>
      </Row>

      {/* What the page knows and how. appLogOrigin says "events" while lwc-logtypes is
          undeployed, which is why the application-log numbers are grouped locally. */}
      <p className="text-muted small mt-4">
        <FormattedMessage
          id="DailyAnalysisGenerated"
          values={{
            when: formatDate(data.generatedAt, "d MMM HH:mm"),
            source: data.appLogOrigin,
            days: data.baselineDays,
          }}
        />
      </p>
    </div>
  );
}

export default DailyAnalysisOverview;
